import PasswordInput from '../components/PasswordInput';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import {
  UserGroupIcon, XMarkIcon, CheckCircleIcon, NoSymbolIcon,
  MagnifyingGlassIcon, PlusIcon, PencilIcon,
} from '@heroicons/react/24/outline';

const parseDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const dt = new Date(d);
  const y = dt.getFullYear();
  const mo = String(dt.getMonth()+1).padStart(2,'0');
  const day = String(dt.getDate()).padStart(2,'0');
  return y + '-' + mo + '-' + day;
};


const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}).replace(/ /g,'-') : '—';
const ini  = (n='') => n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'P';

// #8 — Phone and CNIC formatters
const formatPhone = (v) => {
  const d = v.replace(/\D/g,'');
  if (d.length<=4) return d;
  return d.slice(0,4)+'-'+d.slice(4,11);
};
const formatCnic = (v) => {
  const d = v.replace(/\D/g,'');
  if (d.length<=5) return d;
  if (d.length<=12) return d.slice(0,5)+'-'+d.slice(5);
  return d.slice(0,5)+'-'+d.slice(5,12)+'-'+d.slice(12,13);
};
const digitsOnly = (v) => String(v||'').replace(/\D/g,'');

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .pm*{box-sizing:border-box;} .pm{font-family:'Sora',sans-serif;color:#1a1d2e;animation:pmFade .4s ease both;}
  @keyframes pmFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .pm-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .pm-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .pm-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 16px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;}
  .btn-orange{background:#f97316;color:white;} .btn-orange:hover{box-shadow:0 4px 14px rgba(249,115,22,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-red{background:#fef2f2;color:#ef4444;border:1px solid #fecaca;} .btn-red:hover{background:#ef4444;color:white;}
  .btn-sm{padding:5px 11px;font-size:.74rem;} .btn:disabled{opacity:.5;cursor:not-allowed;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;margin-bottom:1rem;}
  .panel-hdr{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 20px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;}
  .search-wrap{position:relative;flex:1;min-width:180px;max-width:320px;}
  .search-wrap svg{position:absolute;left:10px;top:50%;transform:translateY(-50%);width:14px;height:14px;color:#9094a8;stroke-width:2;}
  .search-input{width:100%;font-family:'Sora',sans-serif;font-size:.8rem;padding:8px 12px 8px 32px;border:1.5px solid #e0e4f0;border-radius:9px;outline:none;background:#f8f9fc;color:#1a1d2e;}
  .search-input:focus{border-color:#f97316;background:white;}
  .tbl{width:100%;border-collapse:collapse;}
  .tbl thead tr{background:#f8f9fc;border-bottom:1px solid #eaecf4;}
  .tbl th{padding:11px 16px;text-align:center !important;font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .tbl th:last-child{text-align:right;}
  .tbl tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .tbl tbody tr:last-child{border-bottom:none;} .tbl tbody tr:hover{background:#fafbff;}
  .tbl td{padding:11px 16px;font-size:.8rem;color:#3a3f58;vertical-align:middle;} .tbl td:last-child{text-align:right;}
  .nc{display:flex;align-items:center;gap:10px;}
  .av{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,#f97316,#ef4444);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;color:white;flex-shrink:0;}
  .nm{font-weight:600;color:#1a1d2e;font-size:.82rem;} .ne{font-size:.68rem;color:#9094a8;margin-top:1px;}
  .mono{font-family:'JetBrains Mono',monospace;font-size:.74rem;color:#6b7194;}
  .pill{display:inline-flex;font-size:.67rem;font-weight:600;padding:2px 9px;border-radius:99px;}
  .pill.active{background:#ecfdf5;color:#10c98e;} .pill.inactive{background:#fef2f2;color:#ef4444;}
  .child-tag{display:inline-flex;font-size:.67rem;font-weight:600;padding:2px 8px;border-radius:99px;background:#fff7ed;color:#f97316;margin:1px;}
  .act-btn{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;margin-left:3px;transition:.13s;}
  .act-btn svg{width:14px;height:14px;stroke-width:1.8;}
  .act-btn.edit{color:#3b6ef8;} .act-btn.edit:hover{background:#eef2ff;border-color:#3b6ef8;}
  .act-btn.deactivate{color:#ef4444;} .act-btn.deactivate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.reactivate{color:#10c98e;} .act-btn.reactivate:hover{background:#ecfdf5;border-color:#10c98e;}
  .act-btn.del{color:#ef4444;} .act-btn.del:hover{background:#fef2f2;border-color:#fecaca;}
  .empty{text-align:center;padding:48px;color:#b0b5cc;font-size:.82rem;}
  .spinner{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#f97316;border-radius:50%;animation:sp .7s linear infinite;}
  @keyframes sp{to{transform:rotate(360deg)}}
  .overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;width:100%;max-height:90vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;animation:mIn .25s ease;}
  @keyframes mIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
  .m-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .m-hdr-l{display:flex;align-items:center;gap:10px;}
  .m-icon{width:34px;height:34px;border-radius:10px;display:flex;align-items:center;justify-content:center;}
  .m-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .m-icon.orange{background:#fff7ed;color:#f97316;} .m-icon.red{background:#fef2f2;color:#ef4444;} .m-icon.green{background:#ecfdf5;color:#10c98e;}
  .m-title{font-size:.95rem;font-weight:700;color:#0f1123;} .m-sub{font-size:.72rem;color:#9094a8;}
  .m-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;}
  .m-close svg{width:14px;height:14px;stroke-width:2;}
  .m-body{padding:22px;overflow-y:auto;flex:1;} .m-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;}
  .fg{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .ff{display:flex;flex-direction:column;gap:5px;} .ff.full{grid-column:1/-1;}
  .fl{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .fi,.fsel{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;transition:.15s;}
  .fi:focus,.fsel:focus{border-color:#f97316;background:white;}
  .fh{font-size:.68rem;color:#b0b5cc;}
  .fdiv{grid-column:1/-1;display:flex;align-items:center;gap:10px;margin:4px 0;}
  .fdiv-lbl{font-size:.65rem;font-weight:700;color:#c0c5dc;text-transform:uppercase;letter-spacing:.09em;white-space:nowrap;}
  .fdiv-line{flex:1;height:1px;background:#f0f2f8;}
  .warn-box{background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:10px 14px;font-size:.76rem;color:#991b1b;margin-bottom:14px;line-height:1.55;}
  .toggle-row{display:flex;align-items:center;gap:8px;font-size:.78rem;color:#6b7194;cursor:pointer;user-select:none;}

  /* Admission no tag input */
  .adm-tag-wrap{display:flex;flex-wrap:wrap;gap:6px;padding:8px 10px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;min-height:42px;align-items:center;cursor:text;}
  .adm-tag-wrap:focus-within{border-color:#f97316;background:white;}
  .adm-tag{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:99px;font-size:.72rem;font-weight:600;background:#fff7ed;color:#f97316;white-space:nowrap;}
  .adm-tag.found{background:#ecfdf5;color:#065f46;}
  .adm-tag.notfound{background:#fef2f2;color:#ef4444;}
  .adm-tag-x{cursor:pointer;opacity:.7;display:flex;align-items:center;}
  .adm-tag-x:hover{opacity:1;}
  .adm-tag-x svg{width:10px;height:10px;stroke-width:2.5;}
  .adm-tag-inner{border:none;outline:none;background:transparent;font-family:'Sora',sans-serif;font-size:.8rem;color:#1a1d2e;min-width:120px;flex:1;padding:2px 0;}
  .adm-lookup-btn{display:inline-flex;align-items:center;gap:4px;padding:4px 10px;border-radius:7px;background:#fff7ed;color:#f97316;border:1px solid #fed7aa;font-family:'Sora',sans-serif;font-size:.72rem;font-weight:600;cursor:pointer;flex-shrink:0;}
  .adm-lookup-btn:hover{background:#f97316;color:white;}
  .adm-lookup-btn svg{width:12px;height:12px;}
  .adm-hint{font-size:.68rem;color:#b0b5cc;margin-top:2px;}
  .student-preview{background:#f0fdf4;border:1px solid #a7f3d0;border-radius:8px;padding:8px 12px;font-size:.76rem;color:#065f46;display:flex;align-items:center;gap:8px;margin-top:4px;}
  .student-preview svg{width:13px;height:13px;flex-shrink:0;}

  .cards{display:none;padding:10px;}
  .card{background:#fafbff;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
  .card-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;gap:8px;}
  .card-row{display:flex;justify-content:space-between;align-items:flex-start;font-size:.76rem;padding:4px 0;border-bottom:1px solid #f0f2f8;gap:8px;}
  .card-row:last-of-type{border-bottom:none;}
  .card-label{color:#9094a8;font-weight:600;white-space:nowrap;flex-shrink:0;}
  .card-actions{display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;justify-content:flex-end;}
  @media(max-width:768px){
    .pm-title{font-size:1.2rem;} .tbl-wrap{display:none!important;} .cards{display:block;}
    .fg{grid-template-columns:1fr;} .ff.full{grid-column:1;}
  }
`;

const FD = ({label}) => <div className="fdiv"><span className="fdiv-lbl">{label}</span><div className="fdiv-line"/></div>;

const emptyForm = {
  name:'', email:'', password:'', phone:'', cnic:'', address:'', relation:'father',
  childAdmissionNos: [], // array of admission nos
};

export default function ParentManagement() {
  const [parents,         setParents]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState('');
  const [showInactive,    setShowInactive]    = useState(false);
  const [modal,           setModal]           = useState(false);
  const [editing,         setEditing]         = useState(null);
  const [form,            setForm]            = useState(emptyForm);
  const [deactivateModal, setDeactivateModal] = useState(null);
  const [deactivateDate,  setDeactivateDate]  = useState('');
  const [reason,          setReason]          = useState('');
  const [saving,          setSaving]          = useState(false);
  const [reactivateConfirm, setReactivateConfirm] = useState(null); // parent
  const [reactivateSaving,  setReactivateSaving]  = useState(false);

  const [admTags,         setAdmTags]         = useState([]); // [{no, found, name, class, section}] — the linked children
  const hasLoaded = useRef(false);

  // Auto-fill from the CNIC-matched student record(s) — see the "fetch by CNIC"
  // effect below, now the only way a child gets linked (the old browse-by-
  // class/section picker let literally any student be linked to any parent with
  // no relation check at all, so it's gone).
  const [autoFillFrom,  setAutoFillFrom]  = useState('');
  const [existingParent, setExistingParent] = useState(null); // a real parent ACCOUNT already on this CNIC
  const [checkingCnic,   setCheckingCnic]   = useState(false);
  // Students found by searching on parentCnic directly — the actual "fetch by CNIC"
  // entry point: type/scan the guardian's CNIC and find every child that lists it,
  // instead of requiring the admin to already know which admission numbers to search.
  const [cnicStudents,        setCnicStudents]        = useState([]);
  const [checkingCnicStudents, setCheckingCnicStudents] = useState(false);
  const studentCache = useRef({});   // admissionNo(upper) -> student record
  const touchedRef   = useRef({});   // fields the user typed/edited manually
  const formRef      = useRef(emptyForm);
  formRef.current = form;
  const setField = (key, val) => {
    touchedRef.current[key] = true;
    setForm(p => ({ ...p, [key]: val }));
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    if (!hasLoaded.current) setLoading(true);
    try { const { data } = await api.get('/admin/parents?includeInactive=true');
      setParents(data||[]); hasLoaded.current = true; }
    catch { toast.error('Failed to load parents'); }
    finally { setLoading(false); }
  };

  const filtered = parents.filter(p => {
    const active = p.user?.isActive !== false;
    if (!showInactive && !active) return false;
    if (search) {
      const q = search.toLowerCase();
      const matchName    = p.user?.name?.toLowerCase().includes(q);
      const matchEmail   = p.user?.email?.toLowerCase().includes(q);
      const matchChild   = (p.children||[]).some(c => c.admissionNo?.toLowerCase().includes(q));
      return matchName || matchEmail || matchChild;
    }
    return true;
  });

  const removeAdmTag = (no) => setAdmTags(prev => prev.filter(t => t.no !== no));

  // Once a CNIC is present (typed or auto-filled), check whether a real parent
  // ACCOUNT already exists for it — not a student's denormalized copy of the field,
  // the actual parents table — so we can offer to link this child to it instead of
  // letting the admin walk into the "CNIC already registered" rejection on submit.
  useEffect(() => {
    if (!modal || editing) { setExistingParent(null); return; }
    const cnicDigits = digitsOnly(form.cnic);
    if (cnicDigits.length < 13) { setExistingParent(null); return; }
    let cancelled = false;
    setCheckingCnic(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/admin/parents/by-cnic?cnic=' + encodeURIComponent(form.cnic));
        if (!cancelled) setExistingParent(data?.parent || null);
      } catch { if (!cancelled) setExistingParent(null); }
      finally { if (!cancelled) setCheckingCnic(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.cnic, modal, editing]);

  // THE actual "fetch by CNIC" step, and now the ONLY way to link a child — the
  // class/section browse-and-search picker was removed (it let literally any
  // student be linked to any parent with no relation check at all). Runs in BOTH
  // add and edit mode: search students directly by their Parent & Guardian CNIC
  // and surface every not-yet-linked match as a one-click "add as child"
  // suggestion, auto-filling still-empty parent fields from the first one.
  useEffect(() => {
    if (!modal) { setCnicStudents([]); return; }
    const cnicDigits = digitsOnly(form.cnic);
    if (cnicDigits.length < 13) { setCnicStudents([]); return; }
    let cancelled = false;
    setCheckingCnicStudents(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/admin/students?parentCnic=' + encodeURIComponent(form.cnic) + '&limit=50');
        if (cancelled) return;
        const matches = data.students || [];
        setCnicStudents(matches);
        matches.forEach(st => { studentCache.current[String(st.admissionNo).toUpperCase()] = st; });
        // Auto-fill whatever's still empty from the first match, same field-merge
        // rules as the admission-number-driven autofill above.
        const cur = formRef.current;
        const patch = {};
        for (const st of matches) {
          const cand = {
            name: st.parentName || '', phone: st.parentContact ? formatPhone(String(st.parentContact)) : '',
            email: st.parentEmail || '', address: st.address || '',
          };
          for (const k of Object.keys(cand)) {
            if (cand[k] && !patch[k] && !cur[k] && !touchedRef.current[k]) patch[k] = cand[k];
          }
        }
        if (Object.keys(patch).length) {
          setForm(prev => {
            const next = { ...prev };
            Object.keys(patch).forEach(k => { if (!prev[k]) next[k] = patch[k]; });
            return next;
          });
          setAutoFillFrom(prev => prev || matches[0]?.parentName || matches[0]?.user?.name || '');
        }
      } catch { if (!cancelled) setCnicStudents([]); }
      finally { if (!cancelled) setCheckingCnicStudents(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cnic, modal]);

  // A student already carrying a self-link (already in admTags — this parent's
  // own existing child) is excluded here either way; anything left with
  // linkedParent set is therefore genuinely linked to a DIFFERENT parent account
  // and must not be silently offered for bulk-add (the backend rejects it too —
  // this is just the same check surfaced before submit instead of after).
  const notYetAdded          = cnicStudents.filter(st => !admTags.some(t => t.no === st.admissionNo));
  const unlinkedCnicStudents = notYetAdded.filter(st => !st.linkedParent);
  const elsewhereLinkedCnicStudents = notYetAdded.filter(st => st.linkedParent);

  const addAllCnicStudents = () => {
    const toAdd = unlinkedCnicStudents.filter(st => !admTags.some(t => t.no === st.admissionNo));
    if (!toAdd.length) return;
    setAdmTags(prev => [
      ...prev,
      ...toAdd.map(st => ({ no: st.admissionNo, found: true, name: st.user?.name || '', class: st.class?.name || '', section: st.section || '' })),
    ]);
    toast.success(`Linked ${toAdd.length} child${toAdd.length === 1 ? '' : 'ren'} found with this CNIC`);
  };

  // Switch the modal into editing the existing parent account, pre-filled with
  // whatever children are already linked plus every admission no. currently typed
  // into this "add" form — so submitting adds the new child(ren) rather than
  // creating a second, duplicate account for the same real parent.
  const linkToExistingParent = () => {
    if (!existingParent) return;
    const existingNos = (existingParent.children || []).map(c => c.admissionNo).filter(Boolean);
    openEdit({
      _id: existingParent._id,
      user: { name: existingParent.name, email: existingParent.email },
      phone: existingParent.phone, cnic: existingParent.cnic,
      address: existingParent.address, relation: existingParent.relation,
      children: [
        ...(existingParent.children || []),
        ...admTags.filter(t => t.found && !existingNos.includes(t.no)).map(t => ({
          admissionNo: t.no, user: { name: t.name }, class: { name: t.class }, section: t.section,
        })),
      ],
    });
    toast.success(`Switched to editing ${existingParent.name}'s existing account`);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    touchedRef.current = {};
    setAutoFillFrom('');
    setExistingParent(null);
    setAdmTags([]);
    setModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setAutoFillFrom('');
    setExistingParent(null);
    const childNos = (p.children||[]).map(c => c.admissionNo||'').filter(Boolean);
    setAdmTags(childNos.map(no => ({
      no,
      found: true,
      name:  (p.children||[]).find(c=>c.admissionNo===no)?.user?.name || '',
      class: (p.children||[]).find(c=>c.admissionNo===no)?.class?.name || '',
      section: (p.children||[]).find(c=>c.admissionNo===no)?.section || '',
    })));
    setForm({
      name:     p.user?.name    || '',
      email:    p.user?.email   || '',
      password: '',
      phone:    p.phone         || '',
      cnic:     p.cnic          || '',
      address:  p.address        || '',
      relation: p.relation      || 'father',
      childAdmissionNos: childNos,
    });
    setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())  { toast.error('Name is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    if (!editing && !form.password.trim()) { toast.error('Password is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        childAdmissionNos: admTags.filter(t=>t.found).map(t=>t.no),
      };
      if (editing) {
        await api.put(`/admin/parents/${editing._id}`, payload);
        toast.success('Parent updated');
      } else {
        const { data } = await api.post('/admin/parents', payload);
        toast.success(data?.message || 'Parent account created', data?.message?.includes('disabled') ? { duration: 7000 } : undefined);
      }
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const openDeactivate = (p) => {
    setDeactivateModal(p); setReason('');
    setDeactivateDate(new Date().toISOString().split('T')[0]);
  };

  const deactivate = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/parents/${deactivateModal._id}/deactivate`, { reason, deactivateDate });
      toast.success('Parent account deactivated');
      setDeactivateModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const reactivate = (p) => setReactivateConfirm(p);

  const confirmReactivate = async () => {
    if (!reactivateConfirm) return;
    setReactivateSaving(true);
    try {
      await api.put(`/admin/parents/${reactivateConfirm._id}/reactivate`);
      toast.success('Reactivated');
      setReactivateConfirm(null);
      load();
    } catch { toast.error('Failed'); }
    finally { setReactivateSaving(false); }
  };

  return (
    <>
      <style>{css}</style>
      <div className="pm">
        <div className="pm-hdr">
          <div><div className="pm-title">Parent Accounts</div><div className="pm-sub">Manage parent portal access and account status.</div></div>
          <button className="btn btn-orange" onClick={openAdd}><PlusIcon/>Add Parent</button>
        </div>

        <div className="panel">
          <div className="panel-hdr">
            <div className="search-wrap">
              <MagnifyingGlassIcon/>
              <input className="search-input" placeholder="Search by name, email or admission no…"
                value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            <label className="toggle-row">
              <input type="checkbox" checked={showInactive} onChange={e=>setShowInactive(e.target.checked)} style={{accentColor:'#f97316'}}/>
              Show deactivated
            </label>
          </div>

          {loading
            ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px',color:'#9094a8',fontSize:'.82rem'}}><span className="spinner"/>Loading…</div>
            : !filtered.length
            ? <div className="empty">No parents found.</div>
            : <div className="tbl-wrap" style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead><tr>
                    <th style={{minWidth:160}}>Parent</th>
                    <th style={{minWidth:120,textAlign:'center'}}>Phone</th>
                    <th style={{minWidth:140,textAlign:'center'}}>CNIC</th>
                    <th style={{minWidth:160,textAlign:'center'}}>Address</th>
                    <th style={{minWidth:90,textAlign:'center'}}>Relation</th>
                    <th style={{minWidth:160,textAlign:'center'}}>Children</th>
                    <th style={{minWidth:80,textAlign:'center'}}>Status</th>
                    <th style={{textAlign:'right',minWidth:100}}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(p => {
                      const active = p.user?.isActive !== false;
                      return (
                        <tr key={p._id}>
                          <td>
                            <div className="nc">
                              <div className="av">{ini(p.user?.name)}</div>
                              <div><div className="nm">{p.user?.name||'—'}</div><div className="ne">{p.user?.email||'—'}</div></div>
                            </div>
                          </td>
                          <td style={{textAlign:'center'}}><span className="mono">{p.phone||'—'}</span></td>
                          <td style={{textAlign:'center'}}><span className="mono">{p.cnic||'—'}</span></td>
                          <td style={{textAlign:'center',fontSize:'.76rem'}}>{p.address||'—'}</td>
                          <td style={{textAlign:'center',textTransform:'capitalize',fontSize:'.78rem'}}>{p.relation||'—'}</td>
                          <td style={{textAlign:'center'}}>
                            <div style={{display:'flex',flexWrap:'wrap',gap:2,justifyContent:'center'}}>
                              {(p.children||[]).map((c,i) => (
                                <span key={i} className="child-tag" title={`${c.class?.name||''} ${c.section?'Sec '+c.section:''}`}>
                                  {c.user?.name||c.admissionNo||'Child'}
                                </span>
                              ))}
                              {!(p.children||[]).length && <span style={{fontSize:'.72rem',color:'#c0c5dc'}}>No children</span>}
                            </div>
                          </td>
                          <td style={{textAlign:'center'}}><span className={`pill ${active?'active':'inactive'}`}>{active?'Active':'Deactivated'}</span></td>
                          <td>
                            <button className="act-btn edit" title="Edit" onClick={()=>openEdit(p)}><PencilIcon/></button>
                            {active
                              ? <button className="act-btn deactivate" title="Deactivate" onClick={()=>openDeactivate(p)}><NoSymbolIcon/></button>
                              : <button className="act-btn reactivate" title="Reactivate" onClick={()=>reactivate(p)}><CheckCircleIcon/></button>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          }

          {!loading && filtered.length > 0 && (
            <div className="cards">
              {filtered.map(p => {
                const active = p.user?.isActive !== false;
                return (
                  <div key={p._id} className="card">
                    <div className="card-top">
                      <div className="nc"><div className="av">{ini(p.user?.name)}</div><div><div className="nm">{p.user?.name||'—'}</div><div className="ne">{p.user?.email||'—'}</div></div></div>
                      <span className={`pill ${active?'active':'inactive'}`}>{active?'Active':'Deactivated'}</span>
                    </div>
                    <div className="card-row"><span className="card-label">Phone</span><span className="mono">{p.phone||'—'}</span></div>
                    <div className="card-row"><span className="card-label">CNIC</span><span className="mono">{p.cnic||'—'}</span></div>
                    <div className="card-row"><span className="card-label">Address</span><span style={{textAlign:'right'}}>{p.address||'—'}</span></div>
                    <div className="card-row"><span className="card-label">Relation</span><span style={{textTransform:'capitalize'}}>{p.relation||'—'}</span></div>
                    <div className="card-row">
                      <span className="card-label">Children</span>
                      <div style={{display:'flex',flexWrap:'wrap',gap:2,justifyContent:'flex-end'}}>
                        {(p.children||[]).map((c,i) => (
                          <span key={i} className="child-tag" title={`${c.class?.name||''} ${c.section?'Sec '+c.section:''}`}>
                            {c.user?.name||c.admissionNo||'Child'}
                          </span>
                        ))}
                        {!(p.children||[]).length && <span style={{fontSize:'.72rem',color:'#c0c5dc'}}>No children</span>}
                      </div>
                    </div>
                    <div className="card-actions">
                      <button className="act-btn edit" title="Edit" onClick={()=>openEdit(p)}><PencilIcon/></button>
                      {active
                        ? <button className="act-btn deactivate" title="Deactivate" onClick={()=>openDeactivate(p)}><NoSymbolIcon/></button>
                        : <button className="act-btn reactivate" title="Reactivate" onClick={()=>reactivate(p)}><CheckCircleIcon/></button>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal && createPortal(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal" style={{maxWidth:520}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon orange"><UserGroupIcon/></div>
                <div>
                  <div className="m-title">{editing?'Edit Parent':'Add Parent'}</div>
                  <div className="m-sub">{editing?'Update parent details.':'Create a new parent account.'}</div>
                </div>
              </div>
              <button className="m-close" onClick={()=>setModal(false)}><XMarkIcon/></button>
            </div>
            <form onSubmit={submit} style={{display:'contents'}} autoComplete="off">
              <div className="m-body">
                <div className="fg">
                  <div className="ff full" style={{marginBottom:2}}>
                    <div style={{fontSize:'.76rem',color:'#9333ea',background:'#faf5ff',border:'1px solid #e9d5ff',borderRadius:10,padding:'9px 12px',lineHeight:1.5}}>
                      🪪 Just enter the guardian's <strong>CNIC</strong> below — we'll automatically find their children on the Students page and link them here. No need to search for students manually.
                    </div>
                  </div>
                  {/* #8 — CNIC with auto-format — moved to the top: this is the only
                      thing that needs typing to find and link children. */}
                  <div className="ff full">
                    <label className="fl">Guardian CNIC</label>
                    <input className="fi" style={{fontFamily:"'JetBrains Mono',monospace"}}
                      placeholder="35201-1234567-1" maxLength={15} autoFocus
                      value={form.cnic}
                      onChange={e=>setField('cnic',formatCnic(e.target.value))}/>
                  </div>
                  {checkingCnicStudents && (
                    <div className="ff full" style={{marginTop:-6}}><span style={{fontSize:'.72rem',color:'#9094a8'}}>Searching students for this guardian CNIC…</span></div>
                  )}
                  {!checkingCnicStudents && cnicStudents.length > 0 && (
                    <div className="ff full" style={{marginTop:-6}}>
                      <div style={{fontSize:'.72rem',color:'#065f46',background:'#f0fdf4',border:'1px solid #a7f3d0',borderRadius:8,padding:'8px 10px'}}>
                        <div style={{marginBottom:(unlinkedCnicStudents.length||elsewhereLinkedCnicStudents.length)?8:0}}>
                          Found {cnicStudents.length} student{cnicStudents.length===1?'':'s'} with this guardian CNIC: {cnicStudents.map(st=>st.user?.name||st.admissionNo).join(', ')}
                        </div>
                        {unlinkedCnicStudents.length > 0 && (
                          <button type="button" className="btn btn-orange btn-sm" onClick={addAllCnicStudents}>
                            Add {unlinkedCnicStudents.length === cnicStudents.length ? 'all' : `remaining ${unlinkedCnicStudents.length}`} as child{unlinkedCnicStudents.length===1?'':'ren'}
                          </button>
                        )}
                        {elsewhereLinkedCnicStudents.length > 0 && (
                          <div style={{marginTop:unlinkedCnicStudents.length?8:0,color:'#c2410c'}}>
                            ⚠ {elsewhereLinkedCnicStudents.map(st => `${st.user?.name||st.admissionNo} is already linked to ${st.linkedParent.parentName}'s account`).join('; ')} — not added here. Remove that link first if this is intentional.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {checkingCnic && (
                    <div className="ff full" style={{marginTop:-6}}><span style={{fontSize:'.72rem',color:'#9094a8'}}>Checking for an existing parent account with this CNIC…</span></div>
                  )}
                  {!editing && existingParent && (
                    <div className="ff full" style={{marginTop:-6}}>
                      <div style={{fontSize:'.72rem',color:'#9333ea',background:'#faf5ff',border:'1px solid #e9d5ff',borderRadius:8,padding:'8px 10px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap'}}>
                        <span>A parent account already exists for this CNIC — <strong>{existingParent.name}</strong>{existingParent.children?.length ? ` (linked to ${existingParent.children.length} child${existingParent.children.length===1?'':'ren'})` : ''}. Creating a new one isn't allowed once you save.</span>
                        <button type="button" className="btn btn-orange btn-sm" onClick={linkToExistingParent}>Link child to this account instead</button>
                      </div>
                    </div>
                  )}

                  <FD label="Account"/>
                  <div className="ff"><label className="fl">Full Name *</label><input className="fi" value={form.name} onChange={e=>setField('name',e.target.value)} required/></div>
                  <div className="ff"><label className="fl">Email *</label><input type="email" className="fi" value={form.email} onChange={e=>setField('email',e.target.value)} required/></div>
                  <div className="ff full">
                    <label className="fl">Password {editing&&<span style={{fontSize:'.65rem',color:'#b0b5cc',fontWeight:400}}>leave blank to keep current</span>}</label>
                    <PasswordInput className="fi" autoComplete="new-password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} {...(!editing&&{required:true})}/>
                  </div>

                  <FD label="Details"/>
                  {autoFillFrom && (
                    <div className="ff full" style={{marginTop:-6}}><span style={{fontSize:'.72rem',color:'#065f46',background:'#f0fdf4',border:'1px solid #a7f3d0',borderRadius:8,padding:'6px 10px'}}>Filled from {autoFillFrom}'s record — you can edit</span></div>
                  )}
                  <div className="ff">
                    <label className="fl">Relation</label>
                    <select className="fsel" value={form.relation} onChange={e=>setForm(p=>({...p,relation:e.target.value}))}>
                      <option value="father">Father</option>
                      <option value="mother">Mother</option>
                      <option value="guardian">Guardian</option>
                      <option value="parent">Other / Parent</option>
                    </select>
                  </div>
                  {/* #8 — Phone with auto-format */}
                  <div className="ff">
                    <label className="fl">Phone</label>
                    <input className="fi" style={{fontFamily:"'JetBrains Mono',monospace"}}
                      placeholder="0300-1234567" maxLength={12}
                      value={form.phone}
                      onChange={e=>setField('phone',formatPhone(e.target.value))}/>
                  </div>
                  <div className="ff full">
                    <label className="fl">Address</label>
                    <input className="fi" placeholder="House/Street, City" value={form.address}
                      onChange={e=>setField('address',e.target.value)}/>
                  </div>

                  {/* Children — populated only via the CNIC search above; no manual
                      class/section browse-and-pick anymore (it let any student be
                      linked to any parent with zero relation check). */}
                  <FD label="Children"/>
                  <div className="ff full">
                    {admTags.length > 0 ? (
                      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                        {admTags.map(tag => (
                          <div key={tag.no} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 10px 5px 12px',borderRadius:99,background:tag.found?'#ecfdf5':'#fef2f2',border:`1.5px solid ${tag.found?'#a7f3d0':'#fecaca'}`,fontSize:'.78rem',fontWeight:600,color:tag.found?'#065f46':'#ef4444'}}>
                            <span>{tag.found && tag.name ? tag.name : tag.no}</span>
                            <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.66rem',opacity:.7}}>({tag.no})</span>
                            <span onClick={()=>removeAdmTag(tag.no)} style={{cursor:'pointer',display:'flex',alignItems:'center',marginLeft:2,opacity:.7}}>
                              <XMarkIcon style={{width:12,height:12,strokeWidth:2.5}}/>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="adm-hint">No children linked yet — enter a CNIC above and matching students will show up here to add.</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="m-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>
                  {saving?'Saving…':editing?'Update':'Add Parent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}

      {/* ── Deactivate Modal ── */}
      {deactivateModal && createPortal(
        <div className="overlay" onClick={e=>e.target===e.currentTarget&&setDeactivateModal(null)}>
          <div className="modal" style={{maxWidth:400}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon red"><NoSymbolIcon/></div>
                <div><div className="m-title">Deactivate Parent</div><div className="m-sub">{deactivateModal.user?.name}</div></div>
              </div>
              <button className="m-close" onClick={()=>setDeactivateModal(null)}><XMarkIcon/></button>
            </div>
            <div className="m-body">
              <div className="warn-box">⚠️ This parent will no longer be able to log in. You can reactivate them at any time.</div>
              <div className="ff" style={{marginBottom:12}}>
                <label className="fl">Deactivation Date</label>
                <input type="date" className="fi" value={deactivateDate} onChange={e=>setDeactivateDate(e.target.value)}/>
              </div>
              <div className="ff">
                <label className="fl">Reason</label>
                <input className="fi" required value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Student left school"/>
              </div>
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={()=>setDeactivateModal(null)}>Cancel</button>
              <button className="btn btn-red" onClick={deactivate} disabled={saving}>{saving?'Saving…':'Deactivate Account'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      <ConfirmModal
        open={!!reactivateConfirm}
        title="Reactivate Parent"
        subtitle={reactivateConfirm?.user?.name}
        message={`Reactivate ${reactivateConfirm?.user?.name}'s account?`}
        confirmLabel="Reactivate"
        danger={false}
        onConfirm={confirmReactivate}
        onCancel={()=>setReactivateConfirm(null)}
        loading={reactivateSaving}
      />
    </>
  );
}